import { type FC } from "react";
import {
  Flex,
  Text,
  UseCheckboxProps,
  useBreakpointValue,
  useCheckbox,
} from "@chakra-ui/react";
import { Check } from "~/icons";

const stylePropsByBreakpoint = {
  base: {
    _checked: {
      bg: "th.actionLavenderPrimary",
    },
    _active: {
      bg: "th.actionLavender2",
    },
  },
  md: {
    _checked: {
      bg: "th.actionLavenderPrimary",
      _hover: {
        bg: "th.actionLavender1",
      },
    },
    _hover: {
      bg: "th.actionLavender1",
    },
    _active: {
      bg: "th.actionLavender2",
      _hover: {
        bg: "th.actionLavender2",
      },
    },
  },
};

interface Props extends UseCheckboxProps {
  index: number;
  supportingText?: string;
  text: string;
}

export const CheckboxCard: FC<Props> = ({
  index,
  supportingText,
  text,
  ...props
}) => {
  const responsiveStylingProps = useBreakpointValue(stylePropsByBreakpoint);
  const { state, getCheckboxProps, getInputProps, getLabelProps, htmlProps } =
    useCheckbox({ ...props });

  const className = `checkbox-card-${index}`;

  return (
    <Flex
      as="label"
      alignItems="center"
      bg="th.white"
      border="1px solid"
      borderColor="th.cream2"
      borderRadius="4px"
      className={className}
      cursor="pointer"
      gap={4}
      justify="flex-start"
      px={4}
      py={6}
      w="full"
      {...responsiveStylingProps}
      {...htmlProps}
      {...getLabelProps()}
    >
      <input {...getInputProps()} hidden />
      <Flex
        alignItems="center"
        bg={state.isChecked ? "th.black" : "th.white"}
        border="1px solid"
        borderColor={state.isChecked ? "th.black" : "th.cream4"}
        borderRadius="md"
        h={5}
        justifyContent="center"
        w={5}
        sx={{
          [`.${className}:hover &`]: {
            borderColor: state.isChecked ? "th.black" : "th.cream3",
          },
        }}
        {...getCheckboxProps()}
      >
        {state.isChecked && <Check color="th.white" h={4} w={4} />}
      </Flex>

      <Flex direction="column" gap={2} w="full">
        <Text color="th.black" variant="th-body1">
          {text}
        </Text>

        {supportingText && (
          <Text color="th.gray4" variant="th-body3">
            {supportingText}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
